// @flow
import React, { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { useRouter } from 'Client/router/useRouter';
import { Price } from 'Components/frameworks/price/Price.jsx';
import { Spinner } from 'Components/frameworks/spinner/Spinner.jsx';
import { Icon } from 'Components/frameworks/icon/Icon.jsx';
import { ICONS } from 'Components/utils/icon/icons';
import { COLORS } from 'Components/utils/color/colors';
import ResponsiveContext from 'Components/utils/responsive/responsiveContext';
import ReactAppContext from 'Context/ReactAppContext';
import { ReactPageContext } from 'Context/pageContext';
import { PriceRemoteContext } from 'Components/widgets/price-remote/PriceRemoteContext';
import {
  getDepartureDate,
  getTripDuration,
  getDepartureCity,
} from 'Store/trip-criteria/tripCriteria.selector';
import { getAdults, getKids } from 'Store/trip-information/storeHelper';
import { type Adults } from 'Store/trip-information/adults/adults.reducer';
import { type Kids } from 'Store/trip-information/kids/kids.reducer';
import { getPriceOfStay } from 'Store/price-remote/price-of-stay/priceOfStay.actions';
import {
  priceOfStaySelector,
  priceOfStayIsLoadingSelector,
} from 'Store/price-remote/price-of-stay/priceOfStay.selector';
import { getBasePrice } from 'Store/calendar/calendar.selector';
import { useDebounce } from 'Src/hooks/use-debounce/useDebounce';
import { t } from 'Src/i18n';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { formatTimestampDate } from 'Utils/date/dateUtils';
type Props = {
  productId: string,
};

export const PriceOfStay = (props: Props) => {
  const appContext = useContext(ReactAppContext);
  const pageContext = useContext(ReactPageContext);
  const [
    { isDetailOpened, isJoyview, isCompact, isOverrideSticky, submitPriceRemoteForm },
    setPriceRemoteContext,
  ] = useContext(PriceRemoteContext);
  const { isAppMounted } = useContext(ResponsiveContext);

  const track = useTracking();
  const dispatch = useDispatch();

  const adults: $ReadOnlyArray<Adults> = useSelector(getAdults);
  const departureCity: string = useSelector(getDepartureCity);
  const departureDate: number = useSelector(getDepartureDate);
  const duration: number = useSelector(getTripDuration);
  const kids: $ReadOnlyArray<Kids> = useSelector(getKids);

  const basePrice = useSelector(getBasePrice);
  const { priceOfStay, priceOfStayError } = useSelector(state => ({
    priceOfStay: priceOfStaySelector(state),
    priceOfStayError: state.priceRemote.priceOfStay.error,
  }));

  const priceOfStayIsLoading = useSelector(priceOfStayIsLoadingSelector);

  const isLoading = priceOfStayIsLoading || !isAppMounted;

  const { productId } = props;
  const {
    location: {
      queryParams: { offer: offerName },
    },
  } = useRouter<{}, ProductQueryParams>();

  //Fixme: refactor kids and adults reducers so that their state does not change unnecessarily
  const debouncedSearchCriteria = useDebounce(
    [
      departureDate,
      departureCity,
      duration,
      JSON.stringify(kids),
      JSON.stringify(adults),
      productId,
    ],
    500,
  );

  useEffect(() => {
    const searchCriteria = {
      productId,
      departureDate,
      departureCity,
      duration,
      // $FlowFixMe
      ...(kids ? { kids } : null),
      ...(adults ? { adults } : null),
      offerName,
    };

    if (departureDate && duration && departureCity) {
      dispatch(getPriceOfStay(appContext, searchCriteria));
    }
  }, debouncedSearchCriteria);

  useEffect(
    () => {
      if (priceOfStay) {
        track({
          actionGA: `realPriceDisplay_${priceOfStay.resource}`,
          categoryGA: pageContext.pageType,
          labelGA: pageContext.pageMeta.resortId,
          resortAdult: adults.length,
          resortChildren: kids.length,
          resortCode: pageContext.pageMeta.resortId,
          resortDepartureCityCode: departureCity,
          resortDepartureDate: formatTimestampDate(departureDate, 'YYYYMMDD'),
          resortNightNumber: duration,
          resortTotalParticipant: adults.length + kids.length,
          valueGA: priceOfStay.bestPrice.value,
        });
      }
    },
    [priceOfStay],
  );

  useEffect(
    () => {
      if (priceOfStayError) {
        track({
          event: 'eventGA',
          actionGA: 'errorPriceRemote',
          categoryGA: pageContext.pageType,
          labelGA: pageContext.pageMeta.resortId,
          nonInteraction: '0',
        });
      }
    },
    [priceOfStayError],
  );

  useEffect(
    () => {
      if (priceOfStay) {
        setPriceRemoteContext({
          isTotalPrice: !!priceOfStay,
        });
      }
    },
    [priceOfStay],
  );

  if (priceOfStayError) {
    const btnCls = classNames('reset-button text-success underline md:ml-1 font-bold', {
      block: isCompact || isOverrideSticky,
    });
    return (
      <div className="block text-center md:flex justify-center items-center md:justify-end lg:mlx-10">
        <span>{t('priceRemote.notAvailable')}</span>
        <button onClick={submitPriceRemoteForm} className={btnCls}>
          {t('priceRemote.notAvailableCTA')}
        </button>
      </div>
    );
  }

  return (
    <div className="lg:flex">
      {!isJoyview ? (
        <div className="hidden lg:flex lg:items-center">
          <Icon color={COLORS.BLUE} name={ICONS.ALL_INCLUSIVE} width="1.5rem" />
          <div className="font-brand text-blue text-md ml-2 rtl:ml-2">
            {t('common.allInclusive')}
          </div>
        </div>
      ) : null}
      {isLoading ? (
        <div className="flex justify-center items-center md:justify-end lg:mx-10">
          <Spinner size="small" />
        </div>
      ) : null}

      {!isLoading &&
        priceOfStay && (
          <div className="flex justify-center lg:mx-10">
            <Price
              bestPrice={priceOfStay.bestPrice}
              currency={priceOfStay.currency}
              initialPrice={priceOfStay.initialPrice}
              theme={isJoyview ? 'joyviewRemote' : 'remote'}
              unit={priceOfStay.unit.label}
              onClick={() => {
                setPriceRemoteContext({ isDetailOpened: !isDetailOpened });
              }}
              isValueClickable
              withAsterisk={false}
            />
          </div>
        )}

      {!isLoading &&
        !priceOfStay &&
        basePrice && (
          <div className="flex justify-center lg:mx-10">
            {basePrice.bestPrice && (
              <span className="flex items-center font-sans font-bold text-sm text-blue px-2">
                {t('price.format.from')}
              </span>
            )}
            <Price
              bestPrice={basePrice.bestPrice}
              currency={basePrice.currency}
              initialPrice={basePrice.initialPrice}
              theme={isJoyview ? 'joyviewRemote' : 'remote'}
              unit={basePrice.unit}
              onClick={() => {
                setPriceRemoteContext({ isDetailOpened: !isDetailOpened });
              }}
              isValueClickable
            />
          </div>
        )}
    </div>
  );
};
